import { applyAudioCrossOrigin, safeAudioPlay } from './audioPlaybackReady';
import { normalizeAppHostedUploadUrl } from './normalizeAppHostedUploadUrl';

let currentAudio: HTMLAudioElement | null = null;
let playingKey: string | null = null;
const listeners = new Set<(key: string | null) => void>();

export function resolveStickyAudioUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  return normalizeAppHostedUploadUrl(url);
}

function notifyPlaying(key: string | null): void {
  playingKey = key;
  listeners.forEach((listener) => listener(key));
}

export function subscribeStickyAudioPlayback(listener: (key: string | null) => void): () => void {
  listeners.add(listener);
  listener(playingKey);
  return () => {
    listeners.delete(listener);
  };
}

export function stopStickyAudioPlayback(options?: { silent?: boolean }): void {
  if (currentAudio) {
    currentAudio.onended = null;
    currentAudio.onerror = null;
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  if (!options?.silent) notifyPlaying(null);
}

export function playStickyAudio(url: string): void {
  const resolved = resolveStickyAudioUrl(url);
  if (!resolved) return;

  stopStickyAudioPlayback({ silent: true });

  const audio = new Audio(resolved);
  applyAudioCrossOrigin(audio, resolved);
  currentAudio = audio;
  notifyPlaying(resolved);

  const clearIfCurrent = () => {
    if (currentAudio !== audio) return;
    currentAudio = null;
    notifyPlaying(null);
  };

  audio.onended = clearIfCurrent;
  audio.onerror = clearIfCurrent;
  void safeAudioPlay(audio).catch(() => {
    /* autoplay policy or missing file */
    clearIfCurrent();
  });
}
